import { MentionItem } from './types';

export const getCaretCoordinates = (element: HTMLTextAreaElement, position: number) => {
  const div = document.createElement('div');
  const style = getComputedStyle(element);

  // Copy styles from textarea to div
  const properties = [
    'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize',
    'fontSizeAdjust', 'lineHeight', 'fontFamily', 'textAlign',
    'textTransform', 'textIndent', 'textDecoration', 'letterSpacing', 'wordSpacing'
  ];

  properties.forEach(prop => {
    div.style[prop as any] = style[prop as any];
  });

  div.style.position = 'absolute';
  div.style.top = '-9999px';
  div.style.left = '-9999px';
  div.style.whiteSpace = 'pre-wrap';
  div.style.wordWrap = 'break-word';

  const textContent = element.value.substring(0, position);
  div.textContent = textContent;

  const span = document.createElement('span');
  span.textContent = element.value.substring(position) || '.';
  div.appendChild(span);

  document.body.appendChild(div);

  const coordinates = {
    top: span.offsetTop + parseInt(style.borderTopWidth),
    left: span.offsetLeft + parseInt(style.borderLeftWidth),
    height: parseInt(style.lineHeight)
  };

  document.body.removeChild(div);
  return coordinates;
};

export const findMentionTrigger = (text: string, cursorPosition: number, triggers: string[]) => {
  const textBeforeCursor = text.substring(0, cursorPosition);

  for (const trigger of triggers) {
    const lastTriggerIndex = textBeforeCursor.lastIndexOf(trigger);

    if (lastTriggerIndex !== -1) {
      const textAfterTrigger = textBeforeCursor.substring(lastTriggerIndex + 1);

      // Check if there's no whitespace after the trigger (still typing the mention)
      if (!textAfterTrigger.includes(' ') && !textAfterTrigger.includes('\n')) {
        return {
          trigger,
          position: lastTriggerIndex,
          query: textAfterTrigger
        };
      }
    }
  }

  return null;
};

export const filterItems = (items: MentionItem[], query: string, maxResults = 10): MentionItem[] => {
  if (!query.trim()) {
    return items.slice(0, maxResults);
  }

  const lowerQuery = query.toLowerCase();

  const filtered = items.filter(item =>
    item.label.toLowerCase().includes(lowerQuery) ||
    item.description?.toLowerCase().includes(lowerQuery)
  );

  // Sort by relevance (exact matches first, then starts with, then contains)
  filtered.sort((a, b) => {
    const aLabel = a.label.toLowerCase();
    const bLabel = b.label.toLowerCase();

    if (aLabel === lowerQuery) return -1;
    if (bLabel === lowerQuery) return 1;

    if (aLabel.startsWith(lowerQuery) && !bLabel.startsWith(lowerQuery)) return -1;
    if (bLabel.startsWith(lowerQuery) && !aLabel.startsWith(lowerQuery)) return 1;

    return 0;
  });

  return filtered.slice(0, maxResults);
};

export const insertMention = (
  text: string,
  mentionText: string,
  triggerPosition: number,
  currentPosition: number
): { newText: string; newCursorPosition: number } => {
  const beforeTrigger = text.substring(0, triggerPosition);
  const afterCursor = text.substring(currentPosition);

  const newText = beforeTrigger + mentionText + ' ' + afterCursor;
  const newCursorPosition = triggerPosition + mentionText.length + 1;

  return { newText, newCursorPosition };
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const formatMentionWithSource = (
  item: MentionItem,
  sourcePath: string[],
  sourceIds: string[],
  options: {
    showSource?: boolean;
    sourceFormat?: 'label' | 'id';
    sourceSeparator?: string;
    pathSeparator?: string;
  } = {}
): string => {
  const {
    showSource = true,
    sourceFormat = 'label',
    sourceSeparator = ':',
    pathSeparator = ':'
  } = options;

  if (!showSource || sourcePath.length === 0) {
    return item.label;
  }

  const pathToUse = sourceFormat === 'id' ? sourceIds : sourcePath;
  const sourcePrefix = pathToUse.join(pathSeparator);

  return `${sourcePrefix}${sourceSeparator}${item.label}`;
};

export const createMentionSelectData = (
  item: MentionItem,
  sourcePath: string[],
  sourceIds: string[],
  trigger: string,
  displayText: string
) => {
  return {
    displayText,
    sourcePath: [...sourcePath],
    sourceIds: [...sourceIds],
    item,
    trigger,
    fullPath: [...sourcePath],
    fullIds: [...sourceIds]
  };
};

export const getThemeClasses = (theme: 'light' | 'dark' | 'auto' = 'auto') => {
  const baseClasses = {
    menu: 'border rounded-lg shadow-lg backdrop-blur-sm',
    menuLight: 'bg-white border-gray-200 text-gray-900',
    menuDark: 'bg-gray-800 border-gray-700 text-gray-100',
    item: 'px-3 py-2 cursor-pointer transition-colors duration-150',
    itemHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
    itemSelected: 'bg-blue-100 dark:bg-blue-900',
    search: 'w-full px-3 py-2 border-b outline-none',
    searchLight: 'border-gray-200 bg-white text-gray-900 placeholder-gray-500',
    searchDark: 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
  };

  if (theme === 'light') {
    return {
      menu: `${baseClasses.menu} ${baseClasses.menuLight}`,
      item: baseClasses.item,
      itemHover: 'hover:bg-gray-100',
      itemSelected: 'bg-blue-100',
      search: `${baseClasses.search} ${baseClasses.searchLight}`
    };
  }

  if (theme === 'dark') {
    return {
      menu: `${baseClasses.menu} ${baseClasses.menuDark}`,
      item: baseClasses.item,
      itemHover: 'hover:bg-gray-700',
      itemSelected: 'bg-blue-900',
      search: `${baseClasses.search} ${baseClasses.searchDark}`
    };
  }

  // Auto theme
  return {
    menu: `${baseClasses.menu} bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100`,
    item: baseClasses.item,
    itemHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
    itemSelected: 'bg-blue-100 dark:bg-blue-900',
    search: `${baseClasses.search} border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400`
  };
};
